<template>
  <v-breadcrumbs class="px-0 pt-1" divider=">">
    <v-breadcrumbs-item>
      <v-btn depressed color="primary" x-small @click="selectOrganization(null)"
        >Global</v-btn
      >
    </v-breadcrumbs-item>

    <!-- Organization -->
    <v-icon small class="mx-1">mdi-chevron-right</v-icon>
    <v-breadcrumbs-item>
      <v-menu v-model="menuOrganization">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            v-on="on"
            v-bind="attrs"
            depressed
            :color="organizationId ? 'primary' : 'primary--text'"
            :disabled="loadingOrganizations"
          >
            {{ selectedOrganization?.name || "Selecione uma Organização" }}
          </v-btn>
        </template>
        <vue-perfect-scrollbar
          :settings="{ suppressScrollX: true, wheelPropagation: false }"
          style="max-height: 65dvh; max-height: 65vh"
        >
          <v-list dense>
            <p class="mx-3 mb-0" v-if="organizations.length === 0">
              Nenhuma organização encontrada
            </p>

            <v-text-field
              v-if="organizations.length >= 3"
              v-model="search.organization"
              label="Pesquisar"
              hide-details
              clearable
              outlined
              autofocus
              dense
              class="mt-1 ma-2"
              @keyup.enter="selectOrganization(filteredOrganizations[0]?.id)"
            />
            <v-list-item
              v-for="org in filteredOrganizations"
              :key="org.id"
              @click="selectOrganization(org.id)"
              :class="{ 'primary--text': org.id === organizationId }"
            >
              <v-list-item-avatar size="40" class="ma-0 mr-2">
                <base-avatar
                  :src="org.photo"
                  :seed="org?.id"
                  avatarStyle="shapes"
                  :size="35"
                />
              </v-list-item-avatar>
              <v-list-item-title>{{ org.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </vue-perfect-scrollbar>
      </v-menu>
    </v-breadcrumbs-item>

    <!-- Party -->
    <v-icon small class="mx-1" v-if="scopeDeep >= 1">mdi-chevron-right</v-icon>
    <v-breadcrumbs-item v-if="scopeDeep >= 1">
      <v-menu v-model="menuParty">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            v-on="on"
            v-bind="attrs"
            depressed
            :color="partyId ? 'primary' : 'primary--text'"
            :disabled="loadingParties"
          >
            {{ selectedParty?.name || "Selecione um Evento" }}
          </v-btn>
        </template>
        <vue-perfect-scrollbar
          :settings="{ suppressScrollX: true, wheelPropagation: false }"
          style="max-height: 65dvh; max-height: 65vh"
        >
          <v-list dense>
            <p class="mx-3 mb-0" v-if="parties.length === 0">
              Nenhum evento encontrado
            </p>

            <v-text-field
              v-if="parties.length >= 3"
              v-model="search.party"
              label="Pesquisar"
              hide-details
              clearable
              autofocus
              outlined
              dense
              class="mt-1 ma-2"
              @keyup.enter="selectParty(filteredParties[0]?.id)"
            />
            <v-list-item
              v-for="party in filteredParties"
              :key="party.id"
              @click="selectParty(party.id)"
              :class="{ 'primary--text': party.id === partyId }"
            >
              <v-img
                :src="party.cover"
                :width="(32 * 16) / 9"
                height="32"
                :aspect-ratio="16 / 9"
                class="mr-2 rounded"
              />
              <v-list-item-title>{{ party.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </vue-perfect-scrollbar>
      </v-menu>
    </v-breadcrumbs-item>

    <!-- Ticket Group -->
    <v-icon small class="mx-1" v-if="scopeDeep >= 2">mdi-chevron-right</v-icon>
    <v-breadcrumbs-item v-if="scopeDeep >= 2">
      <v-menu v-model="menuTicketGroup">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            v-on="on"
            v-bind="attrs"
            depressed
            :color="ticketGroupId ? 'primary' : 'primary--text'"
            :disabled="loadingTicketGroups"
          >
            {{ selectedTicketGroup?.name || "Selecione um Setor" }}
          </v-btn>
        </template>
        <v-list dense>
          <p class="mx-3 mb-0" v-if="ticketGroups.length === 0">
            Nenhum setor encontrado
          </p>

          <v-text-field
            v-if="ticketGroups.length >= 3"
            v-model="search.ticketGroup"
            label="Pesquisar"
            hide-details
            clearable
            autofocus
            outlined
            dense
            class="mt-1 ma-2"
            @keyup.enter="selectTicketGroup(filteredTicketGroups[0]?.id)"
          />
          <v-list-item
            v-for="ticketGroup in filteredTicketGroups"
            :key="ticketGroup.id"
            @click="selectTicketGroup(ticketGroup.id)"
            :class="{ 'primary--text': ticketGroup.id === ticketGroupId }"
          >
            <v-list-item-title>{{ ticketGroup.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-breadcrumbs-item>

    <!-- Ticket Block -->
    <v-icon small class="mx-1" v-if="scopeDeep >= 3">mdi-chevron-right</v-icon>
    <v-breadcrumbs-item v-if="scopeDeep >= 3">
      <v-menu v-model="menuTicketBlock">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            x-small
            v-on="on"
            v-bind="attrs"
            depressed
            :color="ticketBlockId ? 'primary' : 'primary--text'"
            :disabled="loadingTicketBlocks"
          >
            {{ selectedTicketBlock?.name || "Selecione um Bloco" }}
          </v-btn>
        </template>
        <v-list dense>
          <p class="mx-3 mb-0" v-if="ticketBlocks.length === 0">
            Nenhum lote encontrado
          </p>
          <v-text-field
            v-if="ticketBlocks.length >= 3"
            v-model="search.ticketBlock"
            label="Pesquisar"
            hide-details
            clearable
            autofocus
            outlined
            dense
            class="mt-1 ma-2"
            @keyup.enter="selectTicketBlock(filteredTicketBlocks[0]?.id)"
          />
          <v-list-item
            v-for="ticketBlock in filteredTicketBlocks"
            :key="ticketBlock.id"
            @click="selectTicketBlock(ticketBlock.id)"
            :class="{ 'primary--text': ticketBlock.id === ticketBlockId }"
          >
            <v-list-item-title>{{ ticketBlock.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-breadcrumbs-item>
  </v-breadcrumbs>
</template>

<script>
import ORGANIZATION from "@/services/admin/organizations";
import PARTY from "@/services/admin/parties";
export default {
  props: {
    organizationId: {
      type: String,
      default: null,
    },
    partyId: {
      type: String,
      default: null,
    },
    ticketGroupId: {
      type: String,
      default: null,
    },
    ticketBlockId: {
      type: String,
      default: null,
    },
    scopeKeys: {
      type: Array,
      default: () => [
        "organizationId",
        "partyId",
        "ticketGroupId",
        "ticketBlockId",
      ],
    },
  },
  data: () => {
    return {
      loadingOrganizations: false,
      menuOrganization: false,
      organizations: [],

      loadingParties: false,
      menuParty: false,
      parties: [],

      loadingTicketGroups: false,
      menuTicketGroup: false,
      ticketGroups: [],

      loadingTicketBlocks: false,
      menuTicketBlock: false,
      ticketBlocks: [],

      search: {
        organization: "",
        party: "",
        ticketGroup: "",
        ticketBlock: "",
      },
    };
  },

  methods: {
    selectOrganization(id) {
      this.$emit("update:organizationId", id);
      this.selectParty(null);
      this.selectTicketGroup(null);
      this.selectTicketBlock(null);
      this.resetSearch();
      this.menuOrganization = false;
    },
    selectParty(id) {
      this.$emit("update:partyId", id);
      this.selectTicketGroup(null);
      this.selectTicketBlock(null);
      this.resetSearch();
      this.menuParty = false;
    },
    selectTicketGroup(id) {
      this.$emit("update:ticketGroupId", id);
      this.selectTicketBlock(null);
      this.resetSearch();
      this.menuTicketGroup = false;
    },
    selectTicketBlock(id) {
      this.$emit("update:ticketBlockId", id);
      this.resetSearch();
      this.menuTicketBlock = false;
    },

    resetSearch() {
      this.$nextTick(() => {
        this.search = {
          organization: "",
          party: "",
          ticketGroup: "",
          ticketBlock: "",
        };
      });
    },

    async getOrganizations() {
      try {
        this.loadingOrganizations = true;
        const { organizations } = await ORGANIZATION.getOrganizations();
        this.organizations = organizations;
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingOrganizations = false;
      }
    },
    async getParties() {
      try {
        this.loadingParties = true;
        this.parties = [];

        const { parties } = await PARTY.getParties({
          organizationId: this.organizationId,
        });
        this.parties = parties;
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingParties = false;
      }
    },
    async getTicketGroups() {
      try {
        this.loadingTicketGroups = true;
        this.ticketGroups = [];

        const { ticketGroups } = await PARTY.ticketGroups(this.partyId);
        this.ticketGroups = ticketGroups;
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingTicketGroups = false;
      }
    },
    async getTicketBlocks() {
      try {
        this.loadingTicketBlocks = true;
        this.ticketBlocks = [];

        const { ticketBlocks } = await PARTY.ticketBlocks(this.partyId, this.ticketGroupId);
        this.ticketBlocks = ticketBlocks;
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingTicketBlocks = false;
      }
    },
  },
  computed: {
    scopeDeep() {
      if (this.ticketBlockId) return 4;
      if (this.ticketGroupId) return 3;
      if (this.partyId) return 2;
      if (this.organizationId) return 1;
      return 0;
    },

    // Organizations
    filteredOrganizations() {
      return this.organizations.filter((org) =>
        org.name.toLowerCase().includes(this.search.organization.toLowerCase())
      );
    },
    selectedOrganization() {
      return this.organizations.find((org) => org.id === this.organizationId);
    },

    // Parties
    filteredParties() {
      return this.parties.filter((party) =>
        party.name.toLowerCase().includes(this.search.party.toLowerCase())
      );
    },
    selectedParty() {
      return this.parties.find((party) => party.id === this.partyId);
    },

    // Ticket Groups
    filteredTicketGroups() {
      return this.ticketGroups.filter((ticketGroup) =>
        ticketGroup.name
          .toLowerCase()
          .includes(this.search.ticketGroup.toLowerCase())
      );
    },
    selectedTicketGroup() {
      return this.ticketGroups.find(
        (ticketGroup) => ticketGroup.id === this.ticketGroupId
      );
    },

    // Ticket Blocks
    filteredTicketBlocks() {
      return this.ticketBlocks.filter((ticketBlock) =>
        ticketBlock.name
          .toLowerCase()
          .includes(this.search.ticketBlock.toLowerCase())
      );
    },
    selectedTicketBlock() {
      return this.ticketBlocks.find(
        (ticketBlock) => ticketBlock.id === this.ticketBlockId
      );
    },
  },

  watch: {
    organizationId(newVal) {
      this.getParties();
    },
    partyId(newVal) {
      this.getTicketGroups();
    },
    ticketGroupId(newVal) {
      this.getTicketBlocks();
    },
    selectedTicketBlock(newVal) {
      if (!newVal) return;
      this.$root.$emit("fee:preview:price", newVal.price);
    },
  },
  mounted() {
    this.getOrganizations();
  },
};
</script>

<style></style>
